/**
 * Shared registration / Canvas language select metadata (checkout locales).
 * @module braze-configuration/registration-language-field
 */

import type { BrazeAttributeSuggestedOption } from './attribute-field-guide'
import { REGISTRATION_LANGUAGE_SELECT_OPTIONS } from './attribute-field-guide'

export const REGISTRATION_LANGUAGE_FIELD_KEYS = new Set([
  'registration_language',
  'registrationLanguage',
  'language',
])

export function isRegistrationLanguageFieldKey (key: string): boolean {
  return REGISTRATION_LANGUAGE_FIELD_KEYS.has(key)
}

export type SelectMenuOption = {
  value: string
  label: string
}

/**
 * Preset select options plus an "Other" row when the current value is outside the preset list.
 */
export function buildSelectMenuOptions (
  suggestedOptions: readonly BrazeAttributeSuggestedOption[],
  currentValue: unknown,
): SelectMenuOption[] {
  const options = suggestedOptions.map((opt) => ({
    value: opt.value,
    label: opt.label || opt.value,
  }))
  const current = String(currentValue ?? '').trim()
  if (current && !options.some((opt) => opt.value === current)) {
    options.push({ value: current, label: `Other: ${current}` })
  }
  return options
}

export function buildRegistrationLanguageMenuOptions (currentValue: unknown): SelectMenuOption[] {
  return buildSelectMenuOptions(REGISTRATION_LANGUAGE_SELECT_OPTIONS, currentValue)
}

export function registrationLanguageFieldDef (
  key: string,
  label = 'Registration language',
): {
  key: string
  label: string
  inputType: 'select'
  selectOptions: typeof REGISTRATION_LANGUAGE_SELECT_OPTIONS
} {
  return {
    key,
    label,
    inputType: 'select',
    selectOptions: REGISTRATION_LANGUAGE_SELECT_OPTIONS,
  }
}

export function canvasLanguageFieldDef (label = 'Language (Canvas locale)'): {
  key: 'language'
  label: string
  inputType: 'select'
  selectOptions: typeof REGISTRATION_LANGUAGE_SELECT_OPTIONS
} {
  return {
    key: 'language',
    label,
    inputType: 'select',
    selectOptions: REGISTRATION_LANGUAGE_SELECT_OPTIONS,
  }
}
